import React, {
  useEffect,
  useState,
} from "react";

import {
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import {
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
} from "firebase/auth";

import {
  doc,
  getDoc,
  onSnapshot,
} from "firebase/firestore";

import {
  auth,
  db,
} from "./firebase";

import SellerApply from "./sellers/SellerApply";
import ListingItem from "./sellers/ListingItem";
//import SellerRequest from "./sellers/SellerRequest";
import SellerSettings from "./sellers/SellerSettings";
import SellerProducts from "./sellers/SellerProducts";
import SellerOrders from "./sellers/SellerOrders";
import SellerPayments from "./sellers/SellerPayments";
import SellerCategorySuggestions from "./sellers/SellerCategorySuggestions";
import "./SellerPortal.css";


/* =====================================================
   CONFIG
===================================================== */

const provider =
  new GoogleAuthProvider();

provider.setCustomParameters({
  prompt:
    "select_account",
});

/* =====================================================
   SHARED UI
===================================================== */

const pageStyle = {
  minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",
  padding:"clamp(18px,4vw,48px)",
  background:"radial-gradient(circle at 12% 8%,#dce7ff 0,transparent 30%),radial-gradient(circle at 90% 85%,#ffe5d1 0,transparent 28%),linear-gradient(135deg,#f5f7ff,#fffaf6)",
  fontFamily:"Inter,Arial,sans-serif"
};
const cardStyle = {
  width:"100%",maxWidth:500,background:"rgba(255,255,255,.94)",
  border:"1px solid rgba(255,255,255,.9)",borderRadius:30,
  padding:"clamp(26px,5vw,44px)",boxShadow:"0 28px 80px rgba(42,54,110,.14)",textAlign:"center"
};
const buttonStyle = {
  border:"none",cursor:"pointer",width:"100%",padding:"16px 20px",borderRadius:15,
  fontSize:15,fontWeight:850,color:"#fff",background:"linear-gradient(110deg,#394b9b,#7465d4)",
  boxShadow:"0 12px 28px rgba(70,80,170,.24)"
};
const softBtn = {border:"1px solid #e4e8f7",cursor:"pointer",padding:"12px 16px",borderRadius:14,fontWeight:800,background:"#f6f7ff",color:"#45518e"};

/* =====================================================
   USER BADGE
===================================================== */

function UserBadge({
  user,
  onOpenSettings,
}) {
  if (!user)
    return null;

  return (
    <div
      style={{
        display:
          "flex",
        alignItems:
          "center",
        gap: 10,
        padding:
          "8px 12px",
        borderRadius: 999,
        background:
          "rgba(255,255,255,.96)",
        boxShadow:
          "0 14px 30px rgba(0,0,0,.12)",
      }}
    >
      <img
        src={
          user.photoURL ||
          "https://via.placeholder.com/40"
        }
        alt="profile"
        style={{
          width: 42,
          height: 42,
          borderRadius:
            "50%",
          objectFit:
            "cover",
          border:
            "2px solid #e5e7eb",
        }}
      />

      <div
        style={{
          lineHeight:
            1.15,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 900,
            color:
              "#111827",
            maxWidth: 140,
            whiteSpace:
              "nowrap",
            overflow:
              "hidden",
            textOverflow:
              "ellipsis",
          }}
        >
          {user.displayName ||
            "Seller"}
        </div>

        <div
          style={{
            display:
              "flex",
            gap: 8,
            marginTop: 4,
          }}
        >
          <button
            onClick={
              onOpenSettings
            }
            style={{
              border:
                "none",
              background:
                "none",
              padding: 0,
              color:
                "#2563eb",
              fontWeight: 800,
              fontSize: 12,
              cursor:
                "pointer",
            }}
          >
            Settings
          </button>

          <button
            onClick={() =>
              signOut(auth)
            }
            style={{
              border:
                "none",
              background:
                "none",
              padding: 0,
              color:
                "#dc2626",
              fontWeight: 800,
              fontSize: 12,
              cursor:
                "pointer",
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

/* =====================================================
   LOGIN
===================================================== */

function SellerLogin() {
  const [loading,setLoading]=useState(false);
  const login=async()=>{try{setLoading(true);await signInWithPopup(auth,provider);}catch(err){alert(err?.message||"Login failed");}finally{setLoading(false);}};
  return (
    <div className="seller-portal-page seller-login-page" style={pageStyle}>
      <div className="seller-login-layout">
        <section className="seller-login-story">
          <div className="seller-brand-lockup"><span className="seller-brand-mark">P</span><span>PITICS <small>SELLER PARTNER</small></span></div>
          <span className="seller-eyebrow">YOUR BUSINESS. MORE POSSIBILITIES.</span>
          <h1>Turn your products into <em>opportunities.</em></h1>
          <p>Welcome to the PITICS Seller Portal. Manage your products, orders and business journey from one simple workspace.</p>
          <div className="seller-login-points"><span><b>✦</b> Showcase your products</span><span><b>↗</b> Reach more customers</span><span><b>✓</b> Manage your business with clarity</span></div>
          <div className="seller-login-orbit"><span>GROW</span><small>with PITICS</small></div>
        </section>
        <section className="seller-login-card" style={cardStyle}>
          <div className="seller-login-icon">✦</div><span className="seller-eyebrow">SELLER ACCESS</span>
          <h2>Welcome back</h2><p className="seller-login-subtitle">Sign in securely to continue to your seller workspace.</p>
          <button className="seller-google-login" style={buttonStyle} onClick={login} disabled={loading}><span className="seller-google-g">G</span>{loading?"Connecting securely...":"Continue with Google"}<span className="seller-login-arrow">→</span></button>
          <div className="seller-secure-note">🔒 Secure sign-in powered by Google</div>
          <div className="seller-login-footnote">New to PITICS? Sign in to begin your seller application.</div>
        </section>
      </div>
    </div>
  );
}

/* =====================================================
   PENDING
===================================================== */

function SellerPending({user}) {
  return (
    <div className="seller-portal-page seller-status-page" style={pageStyle}>
      <div className="seller-status-card" style={cardStyle}>
        <div className="seller-status-icon">◷</div><span className="seller-eyebrow">APPLICATION STATUS</span>
        <h2>Your seller journey is underway</h2>
        <p>Hi <strong>{user?.displayName||"there"}</strong>, your application has been received and is currently under review.</p>
        <div className="seller-status-track"><span className="status-step status-done">✓</span><i/><span className="status-step status-current">2</span><i/><span className="status-step">3</span></div>
        <div className="seller-status-labels"><span>Submitted</span><span>Under review</span><span>Ready to sell</span></div>
        <div className="seller-status-note">We appreciate your interest in selling with PITICS. Your portal access will update once the review is complete.</div>
        <button className="seller-signout-link" onClick={()=>signOut(auth)}>Sign out</button>
      </div>
    </div>
  );
}

/* =====================================================
   DASHBOARD
===================================================== */

function SellerDashboard({user}) {
  const [activeTab,setActiveTab]=useState("add");
  const navItems=[
    {id:"add",label:"Add Product",short:"Create",icon:"＋",desc:"Create a new listing"},
    {id:"products",label:"My Products",short:"Products",icon:"▦",desc:"Manage your catalogue"},
    {id:"orders",label:"Orders",short:"Orders",icon:"▤",desc:"Review and fulfil orders"},
    {id:"payments",label:"Payments",short:"Payments",icon:"₹",desc:"Track your earnings"},
    {id:"categories",label:"Category Suggestions",short:"Categories",icon:"◇",desc:"Suggest new categories"}
  ];
  const activeItem=navItems.find(x=>x.id===activeTab)||navItems[0];
  return (
    <div className="seller-portal-page seller-dashboard-page">
      <div className="seller-dashboard-shell">
        <header className="seller-topbar">
          <div className="seller-brand-lockup"><span className="seller-brand-mark">P</span><span>PITICS <small>SELLER STUDIO</small></span></div>
          <div className="seller-topbar-right"><span className="seller-live-pill"><i/> Seller workspace</span><UserBadge user={user} onOpenSettings={()=>{window.location.href="/seller/settings";}}/></div>
        </header>
        <section className="seller-welcome-hero">
          <div className="seller-welcome-copy"><span className="seller-eyebrow">YOUR BUSINESS COMMAND CENTRE</span>
            <h1>Good to see you, <em>{(user?.displayName||"Seller").split(" ")[0]}.</em></h1>
            <p>Everything you need to showcase your products, serve customers and grow your business — all in one place.</p>
          </div>
          <div className="seller-welcome-art" aria-hidden="true"><div className="seller-welcome-ring"><div className="seller-welcome-cube">✦</div></div><span className="seller-art-tag seller-art-tag-one">Create</span><span className="seller-art-tag seller-art-tag-two">Grow ↗</span></div>
        </section>
        <div className="seller-workspace">
          <aside className="seller-sidebar"><div className="seller-sidebar-heading">WORKSPACE</div>
            <nav className="seller-side-nav" aria-label="Seller workspace navigation">
              {navItems.map(item=><button key={item.id} type="button" className={`seller-nav-item ${activeTab===item.id?"active":""}`} onClick={()=>setActiveTab(item.id)}>
                <span className="seller-nav-icon">{item.icon}</span><span className="seller-nav-copy"><strong>{item.label}</strong><small>{item.desc}</small></span><span className="seller-nav-chevron">›</span>
              </button>)}
            </nav>
            <div className="seller-sidebar-help"><span>✧</span><strong>Keep growing</strong><p>Great products start with a great presentation. Make every listing count.</p></div>
            <button className="seller-sidebar-settings" type="button" onClick={()=>{window.location.href="/seller/settings";}}>⚙ Account settings <span>→</span></button>
          </aside>
          <main className="seller-main-panel">
            <div className="seller-section-heading"><div><span className="seller-eyebrow">SELLER WORKSPACE / {activeItem.short.toUpperCase()}</span><h2>{activeItem.label}</h2><p>{activeItem.desc}. Keep your business moving with PITICS.</p></div><span className="seller-section-mark">{activeItem.icon}</span></div>
            <div className="seller-dashboard-content" key={activeTab}>
              {activeTab==="add"&&<ListingItem/>}{activeTab==="products"&&<SellerProducts/>}{activeTab==="orders"&&<SellerOrders/>}{activeTab==="payments"&&<SellerPayments/>}{activeTab==="categories"&&<SellerCategorySuggestions/>}
            </div>
          </main>
        </div>
        <footer className="seller-portal-footer"><span>© 2024–2026 Pitics Technologies. All rights reserved.</span><span>Built to help your business move forward <b>✦</b></span></footer>
      </div>
    </div>
  );
}

/* =====================================================
   ROOT
===================================================== */

export default function PSeller() {
  const [
    sellerState,
    setSellerState,
  ] = useState(
    "checking"
  );

  const [user, setUser] =
    useState(null);

  useEffect(() => {
    let unsubReq = null;

    const unsubAuth =
      onAuthStateChanged(
        auth,
        (u) => {
          if (
            unsubReq
          ) {
            unsubReq();
            unsubReq =
              null;
          }

          if (!u) {
            setUser(null);
            setSellerState(
              "not-logged"
            );
            return;
          }

          setUser(u);

          const reqRef =
            doc(
              db,
              "sellerRequests",
              u.uid
            );

          const sellerRef =
            doc(
              db,
              "sellers",
              u.uid
            );

          unsubReq =
            onSnapshot(
              reqRef,
              async (
                reqSnap
              ) => {
                try {
                  if (
                    reqSnap.exists()
                  ) {
                    const status =
                      reqSnap.data()
                        ?.status;

                    if (
                      status ===
                      "pending"
                    ) {
                      setSellerState(
                        "pending"
                      );
                      return;
                    }

                    if (
                      status ===
                      "approved"
                    ) {
                      setSellerState(
                        "approved"
                      );
                      return;
                    }

                    if (
                      status ===
                      "rejected"
                    ) {
                      const sellerSnap =
                        await getDoc(
                          sellerRef
                        );

                      if (
                        sellerSnap.exists() &&
                        sellerSnap.data()
                          ?.status ===
                          "active"
                      ) {
                        setSellerState(
                          "approved"
                        );
                      } else {
                        setSellerState(
                          "apply"
                        );
                      }

                      return;
                    }
                  }

                  const sellerSnap =
                    await getDoc(
                      sellerRef
                    );

                  if (
                    sellerSnap.exists() &&
                    sellerSnap.data()
                      ?.status ===
                      "active"
                  ) {
                    setSellerState(
                      "approved"
                    );
                  } else {
                    setSellerState(
                      "apply"
                    );
                  }
                } catch {
                  setSellerState(
                    "apply"
                  );
                }
              }
            );
        }
      );

    return () => {
      if (unsubReq)
        unsubReq();

      unsubAuth();
    };
  }, []);

  if (
    sellerState ===
    "checking"
  ) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h3>
            Checking
            Seller
            Access...
          </h3>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="login"
        element={
          sellerState ===
          "not-logged" ? (
            <SellerLogin />
          ) : (
            <Navigate
              to=".."
              replace
            />
          )
        }
      />

      <Route
        path="settings"
        element={
          <SellerSettings />
        }
      />

      <Route
        path="/"
        element={
          sellerState ===
          "approved" ? (
            <SellerDashboard
              user={user}
            />
          ) : sellerState ===
            "pending" ? (
            <SellerPending
              user={user}
            />
          ) : sellerState ===
            "apply" ? (
            <SellerApply
              onSubmitted={() =>
                setSellerState(
                  "pending"
                )
              }
            />
          ) : (
            <Navigate
              to="login"
              replace
            />
          )
        }
      />
    </Routes>
  );
}